import { forwardRef, useState, useCallback, useId, useMemo } from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ChevronIcon } from './select/SelectIcons';
import { SelectOptionList } from './select/SelectOptionList';
import { SelectFeedback } from './select/SelectFeedback';
import { useSelectListbox } from './select/useSelectListbox';
import { sizeStyles } from './select/selectStyles';
import type { SelectProps } from './select/selectTypes';

// Oeffentlicher Einstiegspunkt: Die Typen werden hier weiterexportiert, damit
// der Importpfad `@/components/ui` unveraendert bleibt.
export type { SelectOption, SelectProps } from './select/selectTypes';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

/**
 * Select component with Liquid Glass design
 *
 * @example
 * ```tsx
 * <Select
 *   label="Country"
 *   placeholder="Select a country"
 *   options={[
 *     { value: 'us', label: 'United States' },
 *     { value: 'uk', label: 'United Kingdom' },
 *     { value: 'de', label: 'Germany' },
 *   ]}
 *   value={country}
 *   onChange={setCountry}
 * />
 * ```
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      options,
      label,
      placeholder = 'Select an option',
      helperText,
      error,
      size = 'md',
      fullWidth,
      disabled,
      required,
      className,
      name,
      id: providedId,
    },
    ref
  ) => {
    const generatedId = useId();
    const selectId = providedId || generatedId;
    const listboxId = `${selectId}-listbox`;

    const [internalValue, setInternalValue] = useState(defaultValue || '');

    const selectedValue = value !== undefined ? value : internalValue;
    const hasError = Boolean(error);

    const selectedOption = useMemo(
      () => options.find((opt) => opt.value === selectedValue),
      [options, selectedValue]
    );

    // Im unkontrollierten Modus haelt die Komponente den Wert selbst.
    const handleValueChange = useCallback(
      (optionValue: string) => {
        if (value === undefined) {
          setInternalValue(optionValue);
        }
        onChange?.(optionValue);
      },
      [value, onChange]
    );

    const {
      isOpen,
      setIsOpen,
      highlightedIndex,
      setHighlightedIndex,
      setTriggerNode,
      listRef,
      handleSelect,
      handleKeyDown,
    } = useSelectListbox({ options, selectedValue, disabled, onValueChange: handleValueChange });

    return (
      <div className={cn('relative', fullWidth && 'w-full', className)}>
        {/* Hidden native select for form submission */}
        {name && (
          <select name={name} value={selectedValue} onChange={() => {}} className="sr-only">
            <option value="">{placeholder}</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )}

        {/* Label */}
        {label && (
          <label
            htmlFor={selectId}
            className={cn(
              sizeStyles[size].label,
              'block font-medium text-surface-800',
              disabled && 'opacity-50'
            )}
          >
            {label}
            {required && (
              <span className="text-error ml-1" aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}

        {/* Trigger Button */}
        <button
          ref={(node) => {
            setTriggerNode(node);
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          id={selectId}
          type="button"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-labelledby={label ? `${selectId}-label` : undefined}
          aria-invalid={hasError}
          disabled={disabled}
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          className={cn(
            'w-full flex items-center justify-between gap-2',
            'backdrop-blur-xl',
            'bg-white/[0.08]',
            'border',
            hasError
              ? 'border-error/60'
              : isOpen
              ? 'border-primary/60'
              : 'border-white/[0.12]',
            'text-left',
            'transition-all duration-200',
            sizeStyles[size].trigger,
            disabled && 'opacity-50 cursor-not-allowed',
            isOpen && 'ring-2 ring-primary/20'
          )}
        >
          <span className={cn('flex-1 truncate', !selectedOption && 'text-surface-500')}>
            {selectedOption ? (
              <span className="flex items-center gap-2">
                {selectedOption.icon}
                {selectedOption.label}
              </span>
            ) : (
              placeholder
            )}
          </span>
          <ChevronIcon open={isOpen} />
        </button>

        {/* Dropdown */}
        <SelectOptionList
          ref={listRef}
          isOpen={isOpen}
          options={options}
          selectedValue={selectedValue}
          highlightedIndex={highlightedIndex}
          size={size}
          listboxId={listboxId}
          labelledBy={selectId}
          onSelect={handleSelect}
          onHighlight={setHighlightedIndex}
        />

        {/* Helper Text / Error */}
        <SelectFeedback error={error} helperText={helperText} />
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
